import { computed, onBeforeUnmount, ref } from "@vue/composition-api";
import { ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { LiveChatInvitation } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import usePaymentPackage from "@/hooks/usePaymentPackage";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { ERRORS, EVENTS } from "@/const";

export const usePrivateLiveInvitationConsumer = (userId: string) => {
  const { authUserStrict, authorities } = useAuthUser();
  const { findAll: findAllPaymentPackages } = usePaymentPackage();
  const paymentPackages = ref<PaymentPackage[]>([]);
  const privateLivePricePackageId = ref("0");
  const privateLiveInvitations = ref<LiveChatInvitation[]>([]);
  let onInvitationApprovedCb = () => {};

  const privateLivePrice = computed(() => {
    const pricePackage = paymentPackages.value.find(item => item.id === privateLivePricePackageId.value);
    return pricePackage?.amount ?? 0;
  });

  const setOnInvitationApprovedCb = (cb: () => void) => (onInvitationApprovedCb = cb);
  const getPaymentPackages = async () => {
    paymentPackages.value = await findAllPaymentPackages([PaymentPackageEnum.PRIVATE_LIVECHAT]);
  };

  const getPrivateLivePrice = async () => {
    const response = await providers.ContentPriceProvider.search({
      subjects: [{ subjectType: ContentPriceSubjectTypeEnum.PRIVATE_LIVECHAT, subjectId: userId }],
    });
    privateLivePricePackageId.value = response[0]?.packageId ?? "0";
  };

  const getMyPrivateInvitations = async () => {
    if (!checkAuthorities(authorities.value, ["getMyLivechatInvitation"])) return;
    privateLiveInvitations.value = await providers.LivechatsProvider.invitationSearch({
      data: { senderId: authUserStrict.value.id, recipientId: userId },
    });
  };

  const createPrivateLiveInvitation = async () => {
    if (!checkAuthorities(authorities.value, ["createMyLiveChatInvitation"])) return;
    try {
      const response = await providers.LivechatsProvider.invitationCreate({
        data: { senderId: authUserStrict.value.id, recipientId: userId },
      });
      privateLiveInvitations.value = [...privateLiveInvitations.value, response];
      emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("Private show request has been sent") });
    } catch (e) {
      handleRequestError(e, [
        {
          comparator: e => e.details?.messageTemplate === ERRORS.NOT_ENOUGH_FUNDS_PRIVATE_LIVE,
          onSuccessCB: () => emitter.emit(EVENTS.BALANCE_REFILL_SHOW),
        },
      ]);
    }
  };

  const onInvitationDeclined = (notification?: Notification) => {
    if (!notification) return;
    if (notification.subjectId === userId) {
      emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("Private show request has been declined") });
      privateLiveInvitations.value = [];
    }
  };

  const onInvitationApproved = async (notification?: Notification) => {
    if (!notification) return;
    if (notification.subjectId === userId) {
      privateLiveInvitations.value = [];
      onInvitationApprovedCb();
    }
  };

  const getPrice = async () => {
    await Promise.all([getPaymentPackages(), getPrivateLivePrice()]);
  };

  if (config.featurePrivateLives) {
    emitter.on<Notification>(EVENTS.LIVECHAT_INVITATION_DECLINED, onInvitationDeclined);
    emitter.on<Notification>(EVENTS.LIVECHAT_INVITATION_APPROVED, onInvitationApproved);

    onBeforeUnmount(() => {
      emitter.off<Notification>(EVENTS.LIVECHAT_INVITATION_DECLINED, onInvitationDeclined);
      emitter.off<Notification>(EVENTS.LIVECHAT_INVITATION_APPROVED, onInvitationApproved);
    });

    Promise.all([getPrice(), getMyPrivateInvitations()]);
  }

  return {
    privateLiveInvitations,
    privateLivePrice,
    createPrivateLiveInvitation,
    setOnInvitationApprovedCb,
  };
};
